/**
 * Spotlight mock data for local development.
 *
 * Fed into the three Spotlight pages (InsightsPage, InstagramMetricsPage,
 * ProgramImpactPage) when `USE_MOCK_SPOTLIGHT` is true. Lets the editor
 * render real-shape data without hitting AppSync — useful for local
 * design polish and screenshots before backend metrics flow in.
 *
 * Spec: ad-hoc dev tooling. Not part of any committed spec.
 */

// ---------------------------------------------------------------------------
// Toggle — set to false to hit real AppSync queries.
//
// Forced OFF under the test runner (vitest sets import.meta.env.TEST) so the
// Spotlight page tests exercise their AppSync-mock paths rather than this dev
// fixture. Local `npm run dev` (DEV=true, TEST undefined) still gets mocks.
// ---------------------------------------------------------------------------

export const USE_MOCK_SPOTLIGHT = !import.meta.env.TEST;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TODAY = new Date();
const SEASON = TODAY.getFullYear();

function isoDateDaysAgo(days: number): string {
  const d = new Date(TODAY);
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function isoTimestampDaysAgo(days: number): string {
  const d = new Date(TODAY);
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Sparkline points — 12 evenly-spaced weekly samples
// ---------------------------------------------------------------------------

function sparklinePoints(start: number, end: number): { snapshotDate: string; value: number }[] {
  const points: { snapshotDate: string; value: number }[] = [];
  const steps = 12;
  for (let i = 0; i < steps; i++) {
    const progress = i / (steps - 1);
    // Smooth curve with a touch of jitter for visual interest.
    const value = Math.round(start + (end - start) * progress + (Math.sin(i) * 30));
    points.push({
      snapshotDate: isoDateDaysAgo((steps - 1 - i) * 7),
      value: Math.max(0, value),
    });
  }
  return points;
}

// ---------------------------------------------------------------------------
// 12 mock contestants
// ---------------------------------------------------------------------------

export interface MockContestant {
  cognitoSub: string;
  fullName: string;
  instagramUsername: string;
  followersGained: number;
  postsMadeThisSeason: number;
  reachThisSeason: number;
  impressionsThisSeason: number;
  baselineCapturedAt: string;
  lastSnapshotAt: string;
}

export const MOCK_CONTESTANTS: MockContestant[] = [
  { cognitoSub: 'mock-c1',  fullName: 'Aubrey Brenner',   instagramUsername: 'aubrey.brenner',   followersGained: 1842, postsMadeThisSeason: 47, reachThisSeason: 84_320,  impressionsThisSeason: 142_870 },
  { cognitoSub: 'mock-c2',  fullName: 'Brielle Cohen',    instagramUsername: 'brielle.cohen',    followersGained: 1521, postsMadeThisSeason: 38, reachThisSeason: 71_240,  impressionsThisSeason: 118_410 },
  { cognitoSub: 'mock-c3',  fullName: 'Camila Diaz',      instagramUsername: 'camila.diaz',      followersGained: 1390, postsMadeThisSeason: 42, reachThisSeason: 68_910,  impressionsThisSeason: 109_220 },
  { cognitoSub: 'mock-c4',  fullName: 'Daria Eberhart',   instagramUsername: 'daria.eberhart',   followersGained: 1247, postsMadeThisSeason: 35, reachThisSeason: 59_840,  impressionsThisSeason:  98_510 },
  { cognitoSub: 'mock-c5',  fullName: 'Elise Faraday',    instagramUsername: 'elise.faraday',    followersGained: 1108, postsMadeThisSeason: 33, reachThisSeason: 54_120,  impressionsThisSeason:  89_230 },
  { cognitoSub: 'mock-c6',  fullName: 'Faye Gallagher',   instagramUsername: 'faye.gallagher',   followersGained:  982, postsMadeThisSeason: 29, reachThisSeason: 47_380,  impressionsThisSeason:  78_410 },
  { cognitoSub: 'mock-c7',  fullName: 'Gianna Holt',      instagramUsername: 'gianna.holt',      followersGained:  843, postsMadeThisSeason: 28, reachThisSeason: 41_290,  impressionsThisSeason:  67_120 },
  { cognitoSub: 'mock-c8',  fullName: 'Harper Iverson',   instagramUsername: 'harper.iverson',   followersGained:  712, postsMadeThisSeason: 24, reachThisSeason: 36_140,  impressionsThisSeason:  58_390 },
  { cognitoSub: 'mock-c9',  fullName: 'Iris Jansen',      instagramUsername: 'iris.jansen',      followersGained:  604, postsMadeThisSeason: 22, reachThisSeason: 30_870,  impressionsThisSeason:  49_870 },
  { cognitoSub: 'mock-c10', fullName: 'June Klein',       instagramUsername: 'june.klein',       followersGained:  521, postsMadeThisSeason: 19, reachThisSeason: 26_410,  impressionsThisSeason:  42_510 },
  { cognitoSub: 'mock-c11', fullName: 'Kira Lindgren',    instagramUsername: 'kira.lindgren',    followersGained:  427, postsMadeThisSeason: 17, reachThisSeason: 22_180,  impressionsThisSeason:  35_640 },
  { cognitoSub: 'mock-c12', fullName: 'Lena Mendez',      instagramUsername: 'lena.mendez',      followersGained:  348, postsMadeThisSeason: 14, reachThisSeason: 18_410,  impressionsThisSeason:  29_820 },
].map((c) => ({
  ...c,
  baselineCapturedAt: isoTimestampDaysAgo(84),
  lastSnapshotAt: isoTimestampDaysAgo(1),
}));

// ---------------------------------------------------------------------------
// Contestant_Insights_Page (InsightsPage) — single contestant + peer board
// ---------------------------------------------------------------------------

/** The signed-in contestant's view. We pretend the user is the 4th-ranked
 *  contestant (Daria Eberhart) so the highlight row in the peer board is
 *  somewhere visible without being at the top or bottom. */
const ME = MOCK_CONTESTANTS[3];

export const MOCK_CONTESTANT_INSIGHTS = {
  cognitoSub: ME.cognitoSub,
  competitionYear: SEASON,
  instagramUsername: ME.instagramUsername,
  followersGained: ME.followersGained,
  postsMadeThisSeason: ME.postsMadeThisSeason,
  reachThisSeason: ME.reachThisSeason,
  impressionsThisSeason: ME.impressionsThisSeason,
  baselineCapturedAt: ME.baselineCapturedAt,
  lastSnapshotAt: ME.lastSnapshotAt,
  rankByFollowersGained: 4,
  totalContestants: MOCK_CONTESTANTS.length,
};

export const MOCK_PEER_LEADERBOARD = {
  items: MOCK_CONTESTANTS.map((c, idx) => ({
    rank: idx + 1,
    fullName: c.fullName,
    instagramUsername: c.instagramUsername,
    followersGained: c.followersGained,
    postsMadeThisSeason: c.postsMadeThisSeason,
    reachThisSeason: c.reachThisSeason,
    impressionsThisSeason: c.impressionsThisSeason,
    sparklinePoints: sparklinePoints(0, c.followersGained),
  })),
  nextToken: null,
};

// ---------------------------------------------------------------------------
// Admin_Season_Leaderboard_Page (InstagramMetricsPage)
// ---------------------------------------------------------------------------

export const MOCK_SEASON_LEADERBOARD = {
  items: MOCK_CONTESTANTS.map((c) => ({
    cognitoSub: c.cognitoSub,
    fullName: c.fullName,
    instagramUsername: c.instagramUsername,
    followersGained: c.followersGained,
    postsMadeThisSeason: c.postsMadeThisSeason,
    reachThisSeason: c.reachThisSeason,
    impressionsThisSeason: c.impressionsThisSeason,
    baselineCapturedAt: c.baselineCapturedAt,
    lastSnapshotAt: c.lastSnapshotAt,
  })),
  nextToken: null,
};

export const MOCK_GROWTH_SERIES = {
  items: MOCK_CONTESTANTS.map((c) => ({
    cognitoSub: c.cognitoSub,
    fullName: c.fullName,
    instagramUsername: c.instagramUsername,
    points: sparklinePoints(0, c.followersGained),
  })),
};

// ---------------------------------------------------------------------------
// Admin_Program_Aggregate_Page (ProgramImpactPage)
// ---------------------------------------------------------------------------

const TOTAL_FOLLOWERS_GAINED = MOCK_CONTESTANTS.reduce((a, c) => a + c.followersGained, 0);
const TOTAL_POSTS = MOCK_CONTESTANTS.reduce((a, c) => a + c.postsMadeThisSeason, 0);
const TOTAL_REACH = MOCK_CONTESTANTS.reduce((a, c) => a + c.reachThisSeason, 0);
const TOTAL_IMPRESSIONS = MOCK_CONTESTANTS.reduce((a, c) => a + c.impressionsThisSeason, 0);

// 4 mock titleholders held over from prior seasons.
const MOCK_TITLEHOLDERS = [
  { cognitoSub: 'mock-t1', fullName: 'Madeline Vargas',   title: 'Miss Idaho America 2024',           profilePhotoS3Key: null, instagramUsername: 'madeline.vargas',   followerCount:  18_420 },
  { cognitoSub: 'mock-t2', fullName: 'Sienna Park',       title: "Miss Idaho America's Teen 2024",    profilePhotoS3Key: null, instagramUsername: 'sienna.park',       followerCount:  12_840 },
  { cognitoSub: 'mock-t3', fullName: 'Adriana Rivera',    title: 'Miss Boise 2024',                   profilePhotoS3Key: null, instagramUsername: 'adriana.rivera',    followerCount:   8_310 },
  { cognitoSub: 'mock-t4', fullName: 'Brooke Templeton',  title: "Miss Boise's Teen 2024",            profilePhotoS3Key: null, instagramUsername: 'brooke.templeton',  followerCount:   5_290 },
];

export const MOCK_PROGRAM_AGGREGATE = {
  competitionYear: SEASON,
  contestantCount: MOCK_CONTESTANTS.length,
  titleholderCount: MOCK_TITLEHOLDERS.length,
  totalFollowersAcrossProgram:
    MOCK_TITLEHOLDERS.reduce((a, t) => a + t.followerCount, 0) +
    TOTAL_FOLLOWERS_GAINED,
  totalFollowersGainedContestantsOnly: TOTAL_FOLLOWERS_GAINED,
  totalPostsThisSeason: TOTAL_POSTS,
  totalReachThisSeason: TOTAL_REACH,
  totalImpressionsThisSeason: TOTAL_IMPRESSIONS,
  generatedAt: new Date().toISOString(),
  lastSnapshotDate: isoDateDaysAgo(1),
  titleholders: MOCK_TITLEHOLDERS,
};

// 12 weeks of reach data, contestant + titleholder split.
function mockReachWeeks(): { weekStartDate: string; contestantReach: number; titleholderReach: number }[] {
  const weeks: { weekStartDate: string; contestantReach: number; titleholderReach: number }[] = [];
  for (let i = 11; i >= 0; i--) {
    const cBase = 5_000 + i * 1_500 + Math.round(Math.sin(i) * 800);
    const tBase = 2_000 + i * 700 + Math.round(Math.cos(i) * 400);
    weeks.push({
      weekStartDate: isoDateDaysAgo(i * 7),
      contestantReach: Math.max(0, cBase),
      titleholderReach: Math.max(0, tBase),
    });
  }
  return weeks;
}

export const MOCK_REACH_TIMESERIES = {
  competitionYear: SEASON,
  weeks: mockReachWeeks(),
};

export const MOCK_POSTS_PER_WEEK = {
  competitionYear: SEASON,
  weeks: Array.from({ length: 12 }, (_, i) => ({
    weekStartDate: isoDateDaysAgo((11 - i) * 7),
    totalPosts: 18 + Math.round(Math.sin(i) * 6) + i,
  })),
};
